import { useCallback, useEffect, useRef, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import {
  Button,
  Divider,
  Form,
  Header,
  Icon,
  Image,
  Loader,
  Message,
  Modal,
  ModalActions,
  ModalContent,
} from "semantic-ui-react";

import SearchParams from "simplydo/core/searchParams";
import { OpenAPI } from "simplydo/interfaces";
import { Input } from "simplydo/react/component-library";
import { DotPulse } from "simplydo/react/components/DotPulse";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import constants from "utils/constants";
import util from "utils/utils";

import OrganisationChooser from "./OrganisationChooser";

type AccountResponse = OpenAPI.GET<"/accounts">["response"];

function Login() {
  const [otp, setOtp] = useState("");
  const [otpModal, setOtpModal] = useState(false);
  const [userHasRecoveryCodes, setUserHasRecoveryCodes] = useState<
    boolean | null
  >(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [password, setPassword] = useState("");
  const [securityPhrase, setSecurityPhrase] = useState("");
  const [loginExchangeToken, setLoginExchangeToken] = useState("");
  const [otpExchangeToken, setOtpExchangeToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [recoveryCodesModal, setRecoveryCodesModal] = useState(false);
  const [otpRecoveryCode, setOtpRecoveryCode] = useState("");

  const [accountDetails, setAccountDetails] = useState<AccountResponse | null>(
    null,
  );

  const passwordRef = useRef(null);

  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const paramMessage = params.get("message");
  const ssoProtectionToken = params.get("ssoProtectionToken") || "";

  const dispatch = useAppDispatch();
  const onSelectOrganisation = useCallback(
    (org) => dispatch(actions.organisations.receiveUrlOrganisation(org)),
    [dispatch],
  );
  const onUpdateEmail = useCallback(
    (e) => dispatch(actions.entry.updateEmail(e)),
    [dispatch],
  );
  const onUpdateChallengeResponse = useCallback(
    (e) => dispatch(actions.entry.updateChallengeResponse(e)),
    [dispatch],
  );

  const authErrorMessage = useAppSelector((state) => state.auth.errorMessage);
  const utmLink = useAppSelector((state) => state.auth.utmLink);
  const utmChallenge = useAppSelector((state) => state.auth.utmChallenge);
  const email = useAppSelector((state) => state.entry.email);
  const challengeResponse = useAppSelector(
    (state) => state.entry.challengeResponse,
  );

  const organisation = useAppSelector(
    (state) => state.organisations.urlOrganisation,
  );
  const shouldAutoLogin =
    organisation?.code &&
    organisation?.auth?.types?.length === 1 &&
    organisation?.auth?.types[0] !== "standard";

  useEffect(() => {
    if (shouldAutoLogin) {
      const authType = organisation.auth.types[0];
      const redirectAuthTypes = ["microsoft", "linkedin", "apple", "google"];
      if (redirectAuthTypes.includes(authType)) {
        util.loginWithOAuth(authType, organisation.code);
      } else if (authType === "saml") {
        util.loginWithSaml(organisation.code);
      }
    }
  }, [shouldAutoLogin, organisation?.code, organisation?.auth?.types]);

  useEffect(() => {
    const exchangeTokenChecker = setInterval(() => {
      if (loginExchangeToken && document.visibilityState === "visible") {
        api.auth.loginWithToken(
          loginExchangeToken,
          () => {
            clearInterval(exchangeTokenChecker);
          },
          () => {},
        );
      }
    }, 2000);
    return function cleanup() {
      clearInterval(exchangeTokenChecker);
    };
  }, [loginExchangeToken]);

  const authIsEnabled = (authMethod) => {
    // Special case for ssoProtection, which allows standard login when redirected from SSO protection check
    if (paramMessage === "ssoProtectionLoginRequired") {
      return authMethod === "standard";
    }
    return organisation?.auth?.types?.includes(authMethod);
  };

  const ssoIsEnabled = () => {
    // Disable all sso login methods when redirected from SSO protection check
    if (paramMessage === "ssoProtectionLoginRequired") {
      return false;
    }
    const authMethods = organisation?.auth?.types || [];
    return (
      authMethods.length > 1 ||
      (authMethods.length === 1 && authMethods[0] !== "standard")
    );
  };

  const checkAccount = useCallback(
    (withEmail?: string) => {
      const emailToCheck = withEmail || email;
      setErrorMessage("");

      if (!emailToCheck || !emailToCheck.match(constants.emailRegex)) return;
      if (!organisation) {
        return;
      }
      setLoading(true);
      api.auth.verifyAccount(
        organisation._id,
        emailToCheck,
        challengeResponse,
        (data) => {
          setLoading(false);
          setAccountDetails(data);
        },
        (err) => {
          setErrorMessage(err.message);
          setLoading(false);
        },
      );
    },
    [challengeResponse, email, organisation],
  );

  useEffect(() => {
    const emailParam = params.get("email");
    if (emailParam && organisation) {
      params.delete("email");
      setParams(params);
      onUpdateEmail(emailParam);
      checkAccount(emailParam);
    }
  }, [onUpdateEmail, checkAccount, setParams, params, organisation]);

  const login = () => {
    setLoading(true);
    setErrorMessage("");
    api.auth.login(
      {
        email,
        password,
        challengeResponse,
        exchangeToken: otpExchangeToken,
        otp,
        org: organisation?._id,
        utmLink,
        utmChallenge,
        ssoProtectionToken,
        otpRecoveryCode,
      },
      (data) => {
        if (data.otpRequired) {
          setOtpModal(true);
          setUserHasRecoveryCodes(data.userHasRecoveryCodes);
          setOtpExchangeToken(data.exchangeToken);
          setLoading(false);
        } else {
          setRecoveryCodesModal(false);
          setLoading(false);
          const then = params.get("then");
          if (then) {
            navigate(then);
          }
        }
      },
      (err) => {
        setLoading(false);
        setErrorMessage(err.message);
        setOtpModal(false);
        setRecoveryCodesModal(false);
        setOtp("");
        setOtpRecoveryCode("");
      },
    );
  };

  const loginMagicLink = useCallback(() => {
    setLoading(true);
    setErrorMessage("");
    api.auth.requestLoginEmail(
      email,
      ({
        securityPhrase: newSecurityPhrase,
        exchangeToken: newExchangeToken,
      }) => {
        setLoading(false);
        setSecurityPhrase(newSecurityPhrase);
        setLoginExchangeToken(newExchangeToken);
      },
      (err) => {
        setLoading(false);
        setErrorMessage(err.message);
      },
    );
  }, [email]);

  if (!organisation) {
    return <OrganisationChooser />;
  }

  if (organisation?.isBlocked) {
    return (
      <div style={{ maxWidth: 500, display: "block", margin: "0px auto" }}>
        <Message error>
          <p>
            The {organisation.name} licence has now expired. You are no longer
            able to login and access your account. If you have any problems or
            questions, please contact your organisational administrator.
          </p>
          <p>
            If you don't know who your organisational administrator is then you
            can request this by emailing{" "}
            <a href="mailto:support@simplydo.co.uk">support@simplydo.co.uk</a>.
          </p>
        </Message>
      </div>
    );
  }

  if (securityPhrase) {
    return (
      <div style={{ textAlign: "center" }}>
        <DotPulse size={12} duration={2} />
        <h3>Waiting for email authorisation...</h3>
        <p>
          We've sent you an email with a link to authorise this login. If you
          haven't received it, please check your spam folder.
        </p>
        <p>
          <strong>Please do not close this window or refresh the page.</strong>
        </p>

        <Divider />
        <h4>The email should contain this security phrase:</h4>
        <div
          style={{
            marginTop: 5,
            padding: 20,
            background: "rgb(230,230,230)",
            borderRadius: 10,
            textAlign: "center",
          }}
        >
          <strong>{securityPhrase}</strong>
        </div>
      </div>
    );
  }

  if (shouldAutoLogin) {
    return (
      <>
        <Header as="h2">Redirecting to login...</Header>
        <Divider />
        <Loader active inline="centered" />
        <div style={{ height: 5 }} />
      </>
    );
  }

  if (
    authIsEnabled("standard") &&
    !organisation.loginLinkEnabled &&
    accountDetails &&
    !accountDetails.exists &&
    accountDetails.challengePassed
  ) {
    const urlParams = new SearchParams();
    if (params.get("utm-link") || utmLink) {
      urlParams.set("utm-link", params.get("utm-link") || utmLink || "");
    }
    if (params.get("utm-challenge") || utmChallenge) {
      urlParams.set(
        "utm-challenge",
        params.get("utm-challenge") || utmChallenge || "",
      );
    }

    const thenParam = params.get("then");
    if (thenParam && thenParam !== "/") {
      urlParams.set("then", encodeURIComponent(thenParam));
    } else {
      const path = location.pathname + location?.search;
      if (path && path !== "/" && path !== "/login") {
        urlParams.set("then", encodeURIComponent(path));
      }
    }

    return <Navigate to={`/register${urlParams.toSafeString()}`} />;
  }

  return (
    <div>
      <Image
        size="small"
        src={organisation?.darkLogoUrl}
        alt=""
        style={{ margin: "0 auto" }}
      />
      <Header as="h2">
        {organisation?.appName || organisation?.name ?
          t("entry.login.welcome", {
            orgName: organisation?.appName || organisation?.name,
          })
        : `Welcome to ${util.appName()}`}
        {organisation?.welcomeText ?
          <Header.Subheader style={{ whiteSpace: "pre-wrap" }}>
            {organisation.welcomeText}
          </Header.Subheader>
        : null}
      </Header>
      {paramMessage && paramMessage === "ssoProtectionLoginRequired" ?
        <Message info>
          We detected that you are trying to access {util.appName()} using an
          email you had already used before. Please log in with your existing
          account details.
        </Message>
      : null}
      {authErrorMessage ?
        <Message info>
          We need to verify your details to confirm it's you. Please log in
          again.
        </Message>
      : null}
      {!util.localStorageIsSupported() ?
        <Message warning>
          Your device does not support saving data locally. Your login
          information will not be saved when revisiting {util.appName()}.
        </Message>
      : null}

      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {authIsEnabled("saml") && (
          <Button
            size="small"
            fluid
            primary
            icon="key"
            content={
              organisation.auth?.saml?.buttonText ?? t("entry.login.sso")
            }
            onClick={() => util.loginWithSaml(organisation.code)}
          />
        )}
        {authIsEnabled("linkedin") && (
          <Button
            size="small"
            fluid
            color="linkedin"
            icon="linkedin"
            content="Sign in with LinkedIn"
            onClick={() => util.loginWithOAuth("linkedin", organisation.code)}
          />
        )}
        {authIsEnabled("google") && (
          <Button
            size="small"
            fluid
            color="google plus"
            icon="google"
            content="Sign in with Google"
            onClick={() => util.loginWithOAuth("google", organisation.code)}
          />
        )}
        {authIsEnabled("apple") && (
          <Button
            size="small"
            fluid
            color="black"
            icon="apple"
            content="Sign in with Apple"
            onClick={() => util.loginWithOAuth("apple", organisation.code)}
          />
        )}
        {authIsEnabled("microsoft") && (
          <Button
            size="small"
            fluid
            color="msteams"
            icon="microsoft"
            content={
              organisation.auth?.microsoft?.buttonText ??
              "Sign in with Microsoft"
            }
            onClick={() => util.loginWithOAuth("microsoft", organisation.code)}
          />
        )}
      </div>

      {authIsEnabled("standard") && (
        <>
          {ssoIsEnabled() && <Divider horizontal>OR</Divider>}
          <h5 style={{ marginTop: 0, marginBottom: 5 }}>
            Enter your email address to continue
          </h5>
          {errorMessage && !loading && (
            <Message size="small" warning>
              <Icon name="warning circle" />
              {errorMessage}
            </Message>
          )}
          <Form
            style={{ display: "flex", flexDirection: "column", gap: 5 }}
            warning
          >
            <Input
              fluid
              tabIndex="0"
              value={email}
              icon="mail"
              onChange={(e) => {
                onUpdateEmail(e.target.value);
                setPassword("");
                onUpdateChallengeResponse("");
                setAccountDetails(null);
                setErrorMessage("");
              }}
              onKeyDown={(e) => {
                if (e.keyCode === 13) {
                  e.preventDefault();
                  e.stopPropagation();
                  if (
                    accountDetails?.exists &&
                    accountDetails.challengePassed
                  ) {
                    login();
                  } else {
                    checkAccount();
                  }
                }
              }}
              name="email"
              type="email"
              autoComplete="email"
              placeholder={t("placeholder.email")}
            />

            {accountDetails ?
              <>
                {organisation.loginLinkEnabled ?
                  accountDetails.existsInOrganisation ?
                    <Form.Button
                      tabIndex="0"
                      onClick={loginMagicLink}
                      primary
                      fluid
                      loading={loading}
                    >
                      Send magic login link
                    </Form.Button>
                  : <Message warning style={{ marginTop: 0 }}>
                      <Message.Header>
                        Unable to find your account
                      </Message.Header>
                      <Message.Content>
                        This email address is not registered with{" "}
                        {organisation.name}. Please check that it is correct or
                        retry with another email.
                      </Message.Content>
                    </Message>

                : null}

                {!organisation.loginLinkEnabled ?
                  <>
                    {accountDetails.customChallengeRequired ?
                      <Input
                        autoFocus
                        fluid
                        icon="question"
                        label={organisation?.registrationChallenge?.question}
                        defaultValue={challengeResponse}
                        onChange={(e) =>
                          onUpdateChallengeResponse(e.target.value)
                        }
                      />
                    : null}

                    {(
                      !accountDetails.hasStandardAuth &&
                      !accountDetails.customChallengeRequired
                    ) ?
                      <Message info style={{ marginTop: 0 }}>
                        <Message.Header>Password setup required</Message.Header>
                        <Message.Content>
                          To continue signing into {organisation.name} please
                          set up a password first. You will receive an email
                          with instructions to complete this step.
                          <Divider />
                          <Button
                            primary
                            loading={loading}
                            onClick={() => {
                              setLoading(true);
                              api.auth.sendPasswordSetupEmail(
                                email,
                                organisation?._id,
                                () => {
                                  setLoading(false);
                                  toast.success(t("entry.setupPassword.sent"));
                                  setAccountDetails((prev) => ({
                                    ...prev,
                                    hasStandardAuth: true,
                                  }));
                                  setErrorMessage(
                                    "Please check your email for password setup instructions before proceeding.",
                                  );
                                },
                                (err) => {
                                  setLoading(false);
                                  toast.error(err.message);
                                },
                              );
                            }}
                          >
                            Request password setup email
                          </Button>
                        </Message.Content>
                      </Message>
                    : null}

                    {accountDetails.hasStandardAuth ?
                      <>
                        <div
                          style={{
                            visibility:
                              (
                                accountDetails.exists &&
                                accountDetails.challengePassed
                              ) ?
                                "visible"
                              : "hidden",
                            maxHeight:
                              (
                                accountDetails.exists &&
                                accountDetails.challengePassed
                              ) ?
                                "unset"
                              : "0px",
                          }}
                        >
                          <Form.Field
                            control={Input}
                            tabIndex="0"
                            defaultValue={password}
                            onChange={(e) => setPassword(e.target.value)}
                            fluid
                            icon="lock"
                            ref={passwordRef}
                            name="password"
                            type="password"
                            placeholder={t("entry.login.yourPass")}
                            onKeyDown={(e) => {
                              if (e.keyCode === 13) {
                                e.preventDefault();
                                e.stopPropagation();
                                login();
                              }
                            }}
                          />
                        </div>
                        {(
                          accountDetails.exists &&
                          accountDetails.challengePassed
                        ) ?
                          <Form.Button
                            tabIndex="0"
                            fluid
                            loading={loading}
                            primary
                            onClick={login}
                            name="login-button"
                            disabled={password.length === 0}
                          >
                            {t("entry.login.action")}
                          </Form.Button>
                        : null}
                      </>
                    : null}
                  </>
                : null}
              </>
            : null}

            {!accountDetails || accountDetails.customChallengeRequired ?
              <Form.Button
                tabIndex="0"
                fluid
                loading={loading}
                primary
                onClick={() => checkAccount()}
                disabled={!email || !email.match(constants.emailRegex)}
                name="continue-button"
              >
                {t("generic.continue")}
              </Form.Button>
            : null}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: -8,
              }}
            >
              <Link
                style={{ cursor: "pointer", margin: 0 }}
                onClick={(e) => {
                  e.preventDefault();
                  onSelectOrganisation(null);
                  setAccountDetails(null);
                }}
              >
                Not your organisation?
              </Link>
              {(
                !organisation.loginLinkEnabled &&
                accountDetails &&
                accountDetails.hasStandardAuth
              ) ?
                <Link tabIndex={0} to="/password/forgotten">
                  {t("entry.forgottenPassword.title")}
                </Link>
              : null}
            </div>
          </Form>
        </>
      )}

      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={otpModal}
      >
        <Modal.Header>
          <Icon name="lock" style={{ paddingRight: "5px" }} />
          Multi-factor authentication
          <br />
        </Modal.Header>
        <ModalContent>
          <p>
            To complete your login, please enter the six digit one-time password
            generated by your authentication app.
          </p>
          <div style={{ textAlign: "center" }}>
            <Input
              onKeyDown={(e) => {
                if (e.keyCode === 13) {
                  e.preventDefault();
                  e.stopPropagation();
                  login();
                }
              }}
              onChange={(e) => setOtp(e.target.value)}
              value={otp}
              placeholder="123456"
              size="large"
              autoFocus
            />
          </div>
          <Divider hidden />
          <Message info size="small">
            <Message.Content>
              <Icon name="info circle" /> You may have used an authenticator app
              such as Microsoft Authenticator (
              <a
                href="https://apps.apple.com/us/app/microsoft-authenticator/id983156458"
                target="_blank"
                rel="noopener noreferrer"
              >
                iOS
              </a>{" "}
              /{" "}
              <a
                href="https://play.google.com/store/apps/details?id=com.azure.authenticator"
                target="_blank"
                rel="noopener noreferrer"
              >
                Android
              </a>
              ) or Google Authenticator (
              <a
                href="https://apps.apple.com/us/app/google-authenticator/id388497605"
                target="_blank"
                rel="noopener noreferrer"
              >
                iOS
              </a>{" "}
              /{" "}
              <a
                href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                target="_blank"
                rel="noopener noreferrer"
              >
                Android
              </a>
              ) to set up your multi-factor authentication.{" "}
              {!userHasRecoveryCodes &&
                "If you are unable to access your authenticator app, please contact your organisation's administrator for assistance."}
            </Message.Content>
          </Message>
          {userHasRecoveryCodes && (
            <Message size="small">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                Cannot access your authenticator app? You can login with one of
                your recovery codes instead.
                <Button
                  compact
                  size="tiny"
                  onClick={() => {
                    setRecoveryCodesModal(true);
                    setOtpModal(false);
                  }}
                >
                  Use recovery code
                </Button>
              </div>
            </Message>
          )}
        </ModalContent>
        <ModalActions>
          <Button
            content={t("generic.cancel")}
            onClick={() => {
              setOtpModal(false);
              setLoading(false);
              setOtp("");
            }}
          />
          <Button
            primary
            disabled={!otp}
            content={t("generic.continue")}
            loading={loading}
            onClick={login}
          />
        </ModalActions>
      </Modal>
      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={recoveryCodesModal}
      >
        <Modal.Header>
          <Icon name="lock" style={{ paddingRight: "5px" }} />
          Multi-factor authentication recovery codes
        </Modal.Header>
        <ModalContent>
          <p>
            Use one of your one-time recovery codes to finish signing in. If you
            do not have access to your recovery codes, please contact your
            organisation's administrator for assistance.
          </p>
          <Form>
            <Form.Field>
              <label>Recovery code</label>
              <Input
                onKeyDown={(e) => {
                  e.keyCode === 13 && login();
                }}
                onChange={(e) => setOtpRecoveryCode(e.target.value)}
                value={otpRecoveryCode}
                placeholder="Enter recovery code"
                autoFocus
              />
            </Form.Field>
          </Form>
        </ModalContent>
        <ModalActions>
          <Button
            content={t("generic.cancel")}
            onClick={() => setRecoveryCodesModal(false)}
          />
          <Button
            primary
            content={"Verify recovery code"}
            disabled={!otpRecoveryCode}
            loading={loading}
            onClick={login}
          />
        </ModalActions>
      </Modal>
    </div>
  );
}

export default Login;
